import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { ActionEnum, getExpectNumber } from '../../../common'
import {
  ColumnActionHeader,
  ColumnInputHeader,
  ColumnInputRangeHeader,
  ColumnSelectHeader,
  NumberFormat,
  PageTitleComponent,
  TableActionColumnButtonComponent,
  TableComponent,
} from '../../../components'
import { translate } from '../../../languages'
import { FuelEntity, FuelTypeEnum, FuelUnitEnum } from '../../../models'
import { RootState } from '../../../stores'
import {
  loadFuels,
  setAction,
  setFuelFilter,
  setSelectedFuel,
  setShowSearchDrawer,
} from '../../../stores/actions'
import {
  ManageFuelModalComponent,
  SearchFuelDrawerComponent,
} from './components'

export const FuelPage: React.FC = () => {
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { management } = useSelector((state: RootState) => state.common)
  const { fuelFilter, fuels, isFuelsLoaded } = useSelector(
    (state: RootState) => state.fuel
  )
  const [filter, setFilter] = React.useState(fuelFilter)
  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })

  React.useEffect(() => {
    if (isDesktopScreen) {
      dispatch(setShowSearchDrawer(false))
    }
  }, [dispatch, isDesktopScreen])

  React.useEffect(() => {
    dispatch(loadFuels())
    // eslint-disable-next-line
  }, [])

  React.useEffect(() => {
    setFilter(fuelFilter)
  }, [fuelFilter])

  const filteredFuels = React.useMemo(() => {
    const fuelTypes = fuelFilter.types || []
    const fuelUnits = fuelFilter.units || []
    return fuels
      ? fuels.filter(
          (fuel) =>
            fuel.name
              .toLowerCase()
              .includes((fuelFilter.name || '').toLowerCase()) &&
            (!fuelUnits.length ||
              fuelUnits.filter((item) => item === fuel.unit).length) &&
            (!fuelFilter.priceFrom || fuel.price >= fuelFilter.priceFrom) &&
            (!fuelFilter.priceTo || fuel.price <= fuelFilter.priceTo) &&
            // (!fuelFilter.price || fuelFilter.price - fuel.price >= 0) &&
            (!fuelTypes.length ||
              fuelTypes.filter((item) => item === fuel.type).length)
        )
      : []
  }, [fuels, fuelFilter])

  const columns = React.useMemo(
    () => [
      {
        dataIndex: 'id',
        title: translate('fuel.id', language),
        width: 30,
      },
      {
        dataIndex: 'name',
        title: (
          <ColumnInputHeader
            titleKey="fuel.name"
            value={filter.name}
            onChange={(e) =>
              setFilter({
                ...filter,
                name: e.target.value,
              })
            }
          />
        ),
        width: 100,
      },
      {
        dataIndex: 'unit',
        title: (
          <ColumnSelectHeader
            titleKey="fuel.unit"
            values={filter.units}
            onChange={(e) =>
              setFilter({
                ...filter,
                units:
                  e.map((item: string | number) => item.toString()) ||
                  Object.values(FuelUnitEnum),
              })
            }
            options={Object.values(FuelUnitEnum).map((item) => ({
              value: item,
              name: translate(`fuel.unit.${item.toLowerCase()}`, language),
            }))}
          />
        ),
        width: 100,
        render: (_: any, fuel: FuelEntity) =>
          translate(`fuel.unit.${fuel.unit.toLowerCase()}`, language),
      },
      {
        title: (
          <ColumnInputRangeHeader
            titleKey="fuel.price"
            fromValue={filter.priceFrom}
            toValue={filter.priceTo}
            onToChange={(e: any) =>
              setFilter({
                ...filter,
                priceTo: e.target.value,
              })
            }
            onFromChange={(e: any) =>
              setFilter({
                ...filter,
                priceFrom: e.target.value,
              })
            }
            fromKey="common.from"
            toKey="common.to"
          />
        ),
        render: (_: any, fuel: FuelEntity) => (
          <NumberFormat value={fuel.price} suffixKey="common.moneyType" />
        ),
        width: 150,
      },
      {
        dataIndex: 'type',
        title: (
          <ColumnSelectHeader
            titleKey="fuel.type"
            values={filter.types}
            onChange={(e) =>
              setFilter({
                ...filter,
                types:
                  e.map((item: number | string) => item.toString()) ||
                  Object.values(FuelTypeEnum),
              })
            }
            options={Object.values(FuelTypeEnum).map((item) => ({
              value: item,
              name: translate(`fuel.type.${item.toLowerCase()}`, language),
            }))}
          />
        ),
        render: (_: any, fuel: FuelEntity) =>
          translate(`fuel.type.${fuel.type.toLowerCase()}`, language),
        width: 100,
      },
      {
        title: (
          <ColumnActionHeader
            onSearch={() => {
              dispatch(
                setFuelFilter({
                  ...filter,
                  priceFrom: getExpectNumber(
                    true,
                    filter.priceFrom,
                    filter.priceTo
                  ),
                  priceTo: getExpectNumber(
                    false,
                    filter.priceFrom,
                    filter.priceTo
                  ),
                })
              )
            }}
          />
        ),
        render: (_: any, fuel: FuelEntity) => (
          <TableActionColumnButtonComponent
            onViewButtonClick={() => {
              dispatch(setSelectedFuel(fuel))
              dispatch(setAction(ActionEnum.VIEW))
            }}
            onDeleteButtonClick={() => {
              dispatch(setSelectedFuel(fuel))
              dispatch(setAction(ActionEnum.DELETE))
            }}
          />
        ),
        width: 130,
      },
    ],
    [language, filter, dispatch]
  )

  return (
    <div style={{ height: '97.5%' }}>
      {management.showManageModal && <ManageFuelModalComponent />}
      <PageTitleComponent
        hasSearchDrawer
        onCreateButtonClick={() => {
          dispatch(setSelectedFuel())
          dispatch(setAction(ActionEnum.CREATE))
        }}
      />
      {management.showSearchDrawer && <SearchFuelDrawerComponent />}
      <TableComponent
        data={filteredFuels}
        columns={columns}
        loading={!isFuelsLoaded}
      />
    </div>
  )
}
